import type { Action, Dispatch, Store } from '@reduxjs/toolkit';
import { ReactiveController, ReactiveControllerHost } from 'lit';
import { Unsubscribe } from 'redux';
import type { Persistor } from 'redux-persist';

export abstract class PersistedReduxController<S> implements ReactiveController {
  protected host: ReactiveControllerHost;
  protected store: Store<S, Action>;
  private _persistor: Persistor;
  private _state: S;
  private _persistorUnsubscribe!: Unsubscribe;
  private _storeUnsubscribe!: Unsubscribe;
  private _dispatch: Dispatch<Action>;
  
  public initialized = false;

  constructor(host: ReactiveControllerHost, store: Store<S, Action>, persistor: Persistor) {
    (this.host = host).addController(this);
    this._persistor = persistor;
    this.store = store;
    this._state = store.getState();
    this._dispatch = store.dispatch;
  }

  get dispatch(): Dispatch<Action> {
    return this._dispatch;
  }

  get state(): S {
    return this._state;
  }
  
  hostConnected(): void {
    const { bootstrapped } = this._persistor.getState();
    if (bootstrapped && !this.initialized) {
      return this.initialize();
    }


    this._persistorUnsubscribe = this._persistor.subscribe(() => {
      const { bootstrapped } = this._persistor.getState();
      if (bootstrapped && !this.initialized) {
        this.initialize();
        this._persistorUnsubscribe?.call(this);
      }
    });
  }

  hostDisconnected(): void {
    this._persistorUnsubscribe?.call(this);
    this._storeUnsubscribe?.call(this);
  }

  initialize(): void {
    this.initialized = true;
    this._storeUnsubscribe = this.store.subscribe(() =>
      this.stateChanged(this.store.getState(), false)
    );
    this.stateChanged(this.store.getState(), true);
  }

  abstract stateChanged(state: S, forceUpdate: boolean): void;

  protected updateState(state: S): void {
    this._state = state;
  }
}
